import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "../../contexts/AuthContext";
import clsx from "clsx";

const Sidebar = ({
  isOpen,
  onClose,
  currentPath,
  collapsed = false,
  onToggleCollapse,
}) => {
  const { user, isAdmin } = useAuth();

  const navigation = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: "home",
      current: currentPath === "/dashboard",
      description: "General AI chat & proposal selection",
      showToAll: true, // Always show
    },
    {
      name: "RFP Research",
      href: "/rfp-research",
      icon: "search",
      current: currentPath.startsWith("/rfp-research"),
      description: "Analyze RFPs and get bid recommendations",
      showToAll: true, // Always show
    },
    {
      name: "Proposal Chat",
      href: "/proposal-chat",
      icon: "comment",
      current: currentPath === "/proposal-chat",
      description: "Select a proposal to chat with AI",
      showToAll: true, // Always show
    },
    {
      name: "Proposal Development",
      href: "/proposal-development",
      icon: "file-alt",
      current: currentPath === "/proposal-development",
      description: "Create and edit proposal documents",
      showToAll: true, // Always show
    },
    {
      type: "separator", // Separator before admin items
    },
    {
      name: "All Proposals",
      href: "/proposals",
      icon: "file",
      current:
        currentPath.startsWith("/proposals") &&
        !currentPath.includes("/workspace"),
      description: "Browse and manage all proposals",
      showToAll: false, // Only show to admins
      adminOnly: true,
    },
    // {
    //   name: "All Chats",
    //   href: "/chat",
    //   icon: "robot",
    //   current: currentPath.startsWith("/chat"),
    //   description: "View all chat sessions",
    //   showToAll: true,
    // },
  ];

  // Add admin navigation if user is admin
  if (isAdmin()) {
    navigation.push({
      name: "Admin Panel",
      href: "/admin",
      icon: "cog",
      current: currentPath.startsWith("/admin"),
      description: "System administration",
      showToAll: false,
      adminOnly: true,
    });
  }

  // Filter navigation items based on user role
  const filteredNavigation = navigation.filter((item) => {
    if (item.showToAll) {
      return true; // Show to all users
    }
    if (item.adminOnly) {
      return isAdmin(); // Only show to admins
    }
    return true; // Default: show to all
  });

  const sidebarClasses = clsx(
    "fixed top-16 bottom-0 left-0 z-20 bg-white shadow-xl border-r border-slate-200 flex flex-col transform transition-all duration-300 ease-in-out lg:translate-x-0",
    {
      "translate-x-0": isOpen,
      "-translate-x-full": !isOpen,
      "w-64": !collapsed || !isOpen, // Full width on mobile or when expanded
      "lg:w-16": collapsed, // Collapsed width on desktop
      "lg:w-64": !collapsed, // Expanded width on desktop
    }
  );

  return (
    <div className={sidebarClasses}>
      {/* Mobile close button - only show on mobile */}
      <div className="lg:hidden flex items-center justify-end px-6 py-4 border-b border-slate-200 flex-shrink-0">
        <button
          onClick={onClose}
          className="p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100"
        >
          <FontAwesomeIcon icon="times" />
        </button>
      </div>

      {/* Sidebar toggle button - desktop only */}
      <div className="hidden lg:flex items-center justify-center py-4 border-b border-slate-200 flex-shrink-0">
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 transition-colors duration-200"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FontAwesomeIcon
            icon={collapsed ? "chevron-right" : "chevron-left"}
          />
        </button>
      </div>

      {/* Navigation - Scrollable middle section */}
      <nav className="flex-1 overflow-y-auto py-6 px-3">
        <div className="space-y-1">
          {filteredNavigation.map((item, index) => {
            // Render separator
            if (item.type === "separator") {
              return (
                <div
                  key={`separator-${index}`}
                  className="my-4 border-t border-slate-200"
                />
              );
            }

            // Render navigation link
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={clsx(
                  "group flex items-center text-sm font-medium rounded-lg transition-colors duration-200",
                  {
                    "bg-primary-50 text-primary-700 border-r-2 border-primary-500":
                      item.current,
                    "text-slate-700 hover:text-primary-700 hover:bg-slate-100":
                      !item.current,
                    "px-3 py-3": !collapsed, // Full padding when expanded
                    "lg:px-3 lg:py-3 lg:justify-center": collapsed, // Centered when collapsed on desktop
                    "px-3 py-3": true, // Always full padding on mobile
                  }
                )}
                title={collapsed ? `${item.name} - ${item.description}` : ""} // Show tooltip when collapsed
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={clsx("text-sm", {
                    "text-primary-500": item.current,
                    "text-slate-400 group-hover:text-primary-500":
                      !item.current,
                    "mr-3": !collapsed, // Margin when expanded
                    "lg:mr-0": collapsed, // No margin when collapsed on desktop
                    "mr-3": true, // Always margin on mobile
                  })}
                />
                <div
                  className={clsx("flex-1", {
                    hidden: collapsed && "lg:hidden", // Hide text when collapsed on desktop
                    "lg:block": !collapsed, // Show text when expanded on desktop
                  })}
                >
                  <span>{item.name}</span>
                  {!collapsed && (
                    <div className="text-xs text-slate-500 mt-1">
                      {item.description}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
